
import { SystemConfig } from '../types'; 

// Helper para extrair JSON de strings sujas
const extractJSON = (text: string) => {
    try {
        const firstBrace = text.indexOf('{');
        const lastBrace = text.lastIndexOf('}');
        if (firstBrace === -1 || lastBrace === -1) throw new Error("JSON não encontrado");
        return JSON.parse(text.substring(firstBrace, lastBrace + 1));
    } catch (e) {
        console.error("Erro ao processar resposta da IA:", text);
        throw new Error("A IA retornou um formato de dados ilegível.");
    }
};

const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve((reader.result as string).split(',')[1]);
    reader.onerror = error => reject(error);
  });
};

const callGeminiRest = async (apiKey: string, file: File, prompt: string) => {
    const base64Data = await fileToBase64(file);
    const response = await fetch(`https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash-exp:generateContent?key=${apiKey}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        contents: [{
          parts: [
            { text: prompt },
            { inline_data: { mime_type: file.type, data: base64Data } }
          ]
        }],
        generationConfig: { response_mime_type: "application/json" }
      })
    });

    if (!response.ok) {
        const err = await response.json();
        throw new Error(`Erro API: ${err.error?.message || response.statusText}`);
    }

    const data = await response.json();
    return extractJSON(data.candidates?.[0]?.content?.parts?.[0]?.text || "{}");
};

export const parseHourlyChart = async (file: File, systemConfig: SystemConfig): Promise<number[]> => {
  const apiKey = systemConfig.geminiApiKey;
  if (!apiKey) throw new Error("Chave Gemini não configurada.");

  try {
    const prompt = `
      Analise este gráfico de pedidos por hora de um sistema PDV.
      ESTRATÉGIA: Identifique o volume relativo para cada uma das 24 horas (00h até 23h).
      SAÍDA: JSON com a chave "hourlyValues" contendo um array de 24 números decimais representando a altura das barras ou pontos.
    `;

    const data = await callGeminiRest(apiKey, file, prompt);
    const rawValues: number[] = data.hourlyValues || data.valores_horarios || [];
    const total = rawValues.reduce((a: number, b: number) => a + b, 0);
    const normalized = rawValues.map((v: number) => total > 0 ? v / total : 0);
    while (normalized.length < 24) normalized.push(0);
    return normalized.slice(0, 24);
  } catch (error: any) {
    throw new Error(`Oráculo Horário: ${error.message}`);
  }
};

export const parseWeeklyChart = async (file: File, systemConfig: SystemConfig): Promise<{ weights: Record<number, number>, average: number }> => {
  const apiKey = systemConfig.geminiApiKey;
  if (!apiKey) throw new Error("Chave Gemini não configurada.");

  try {
    const prompt = `
      Analise este gráfico de vendas semanais.
      ESTRATÉGIA: Extraia o volume de pedidos para cada dia da semana (Domingo a Sábado).
      O gráfico está em português (Dom, Seg, Ter...).
      SAÍDA: Retorne um JSON onde as chaves sejam os índices (0 para Domingo, 6 para Sábado) ou os nomes em inglês.
      Exemplo: {"0": 550, "1": 180, "2": 150, "3": 290, "4": 320, "5": 360, "6": 410}
    `;

    const data = await callGeminiRest(apiKey, file, prompt);
    
    // Mapeamento resiliente (aceita "0", 0, "sunday", "domingo", etc)
    const getVal = (keys: string[]) => {
        for (const k of keys) {
            if (data[k] !== undefined) return Number(data[k]);
        }
        return 0;
    };

    const dayMap = [
        getVal(["0", "sunday", "domingo", "dom"]),
        getVal(["1", "monday", "segunda", "seg"]),
        getVal(["2", "tuesday", "terça", "ter"]),
        getVal(["3", "wednesday", "quarta", "qua"]),
        getVal(["4", "thursday", "quinta", "qui"]),
        getVal(["5", "friday", "sexta", "sex"]),
        getVal(["6", "saturday", "sábado", "sab"])
    ];
    
    const totalVolume = dayMap.reduce((a, b) => a + b, 0);
    const averageDaily = totalVolume / 7;
    const weights: Record<number, number> = {};
    dayMap.forEach((val, idx) => { weights[idx] = averageDaily > 0 ? val / averageDaily : 1.0; });
    return { weights, average: averageDaily };
  } catch (error: any) {
    throw new Error(`Oráculo Semanal: ${error.message}`);
  }
};
